import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { isPlainObject } from "lodash-es";

import { TableBlockMultiColumnRecord } from "src/cases/queries/casesTypes";
import {
  TableColumnConfigInputDisplayConfig,
  TableColumnConfigOutput,
  TableCurrencyCode,
  TableEditMode,
} from "src/clients/case-management";
import { TYPE_ICONS } from "src/utils/constants";
import { isISO8601 } from "src/utils/datetime";

export type ColumnsConfig =
  | Record<string, TableColumnConfigOutput>
  | null
  | undefined;

export const isColumnEditable = (
  columnKey: string,
  columnsConfig: ColumnsConfig,
  defaultColumnsEditMode?: TableEditMode,
): boolean => {
  if (!columnsConfig) return true;
  const config = columnsConfig[columnKey];
  if (!config) return defaultColumnsEditMode !== TableEditMode.READ_ONLY;
  return config.edit_mode !== TableEditMode.READ_ONLY;
};

export const getColumnDisplayName = (
  columnKey: string,
  columnsConfig: ColumnsConfig,
): string => {
  const config = columnsConfig?.[columnKey];
  return config?.display_name || columnKey;
};

export const getColumnDisplayType = (
  columnKey: string,
  columnsConfig: ColumnsConfig,
): string | null => {
  const config = columnsConfig?.[columnKey];
  return config?.display_config?.type ?? null;
};

// String cells auto-size between these bounds; the resize handle clamps to
// them and the editor textarea grows within them.
export const MIN_STRING_CELL_HEIGHT = 32;
export const MAX_STRING_CELL_HEIGHT = 300;

// Both tables give every column the same weight and cap: widths stay
// equal, and cell content can never change them.
export const TABLE_COLUMN_MIN_WIDTH = 160;
export const TABLE_COLUMN_WIDTH = 200;

export type CellDisplayType =
  | { type: "string" }
  | { type: "number" }
  | { type: "boolean" }
  | { type: "date" }
  | { type: "datetime" }
  | { type: "currency"; symbol: TableCurrencyCode }
  | { type: "enum" }
  | { type: "tags" }
  | { type: "list" }
  | { type: "object" };

export const formatCurrency = (value: number, currencyCode: string): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
  }).format(value);

const VALID_CURRENCY_CODES: ReadonlySet<string> = new Set(
  Object.values(TableCurrencyCode),
);

type CurrencyValue = { amount: number | null; symbol: TableCurrencyCode };

const isCurrencyObject = (value: unknown): value is CurrencyValue => {
  if (!isPlainObject(value)) return false;
  const obj = value as Record<string, unknown>;
  return (
    "symbol" in obj &&
    typeof obj.symbol === "string" &&
    VALID_CURRENCY_CODES.has(obj.symbol) &&
    "amount" in obj &&
    (typeof obj.amount === "number" || obj.amount === null)
  );
};

/** A value's own symbol wins over the column's configured one, which is how a
 *  per-cell symbol change survives a save (type overrides don't). */
export const resolveCurrencySymbol = (
  value: unknown,
  configuredSymbol: TableCurrencyCode,
): TableCurrencyCode =>
  isCurrencyObject(value) ? value.symbol : configuredSymbol;

export const extractCurrencyAmount = (value: unknown): number | null => {
  if (typeof value === "number") return value;
  if (isPlainObject(value)) {
    const amount = (value as Record<string, unknown>).amount;
    if (typeof amount === "number") return amount;
  }
  if (value === null || value === undefined) return null;
  const num = Number(value);
  return isNaN(num) ? null : num;
};

export const getAllKeys = (records: TableBlockMultiColumnRecord): string[] =>
  Array.from(new Set(records.flatMap((record) => Object.keys(record))));

/** Handles circular references or other values that JSON.stringify throws on. */
export const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value) ?? "{}";
  } catch {
    return "{}";
  }
};

export const getClearedValue = (
  type: CellDisplayType["type"],
): unknown | undefined => {
  if (type === "enum") return null;
  if (type === "tags") return [];
  return undefined;
};

export const isExplicitActivationCell = (
  type: CellDisplayType["type"],
): boolean =>
  type === "list" ||
  type === "object" ||
  type === "enum" ||
  type === "tags" ||
  type === "boolean" ||
  type === "date" ||
  type === "datetime";

export const isValueCompatible = (
  value: unknown,
  displayType: CellDisplayType,
): boolean => {
  switch (displayType.type) {
    case "string":
    case "enum":
      return typeof value !== "object" || value === null;
    case "number":
      return (
        typeof value === "number" ||
        (typeof value === "string" && !isNaN(Number(value)))
      );
    case "boolean":
      return typeof value === "boolean";
    case "date":
    case "datetime":
      return typeof value === "string" && isISO8601(value);
    case "currency": {
      if (isPlainObject(value)) {
        const obj = value as Record<string, unknown>;
        if (!("amount" in obj)) return false;
        return obj.amount === null || typeof obj.amount === "number";
      }
      return extractCurrencyAmount(value) !== null;
    }
    case "tags":
      return Array.isArray(value) || typeof value !== "object";
    case "list":
    case "object":
      return true;
  }
};

export const resolveEffectiveType = (
  value: unknown,
  configuredType: CellDisplayType,
): CellDisplayType => {
  if (value === null || value === undefined) return configuredType;
  if (isValueCompatible(value, configuredType)) return configuredType;
  return resolveColumnType(null, value);
};

/**
 * The type a cell is shown as: an override wins, then a currency value's own
 * type — overrides aren't persisted, so a saved per-cell currency has to keep
 * its type and symbol — then the column's declared type.
 */
export const resolveCellDisplayType = (
  value: unknown,
  declaredType: CellDisplayType,
  typeOverride?: CellDisplayType,
): CellDisplayType => {
  const type =
    typeOverride ??
    (isCurrencyObject(value)
      ? { type: "currency" as const, symbol: value.symbol }
      : declaredType);

  return type.type === "currency"
    ? { type: "currency", symbol: resolveCurrencySymbol(value, type.symbol) }
    : type;
};

/** Whether a cell renders string content — either the declared column type
 *  is string, or the value doesn't match the declared type and resolves to
 *  one. Both table views key their string-cell chrome off this. */
export const isStringCell = (
  value: unknown,
  displayType: CellDisplayType,
): boolean => resolveEffectiveType(value, displayType).type === "string";

export const resolveColumnType = (
  displayConfig: TableColumnConfigInputDisplayConfig | null | undefined,
  value: unknown,
): CellDisplayType => {
  if (displayConfig != null && displayConfig.type != null) {
    switch (displayConfig.type) {
      case "currency":
        return {
          type: "currency",
          symbol: displayConfig.symbol,
        };
      case "boolean":
      case "date":
      case "datetime":
      case "enum":
      case "list":
      case "number":
      case "object":
      case "string":
      case "tags":
        return { type: displayConfig.type };
    }
  }

  if (value === null || value === undefined) {
    return { type: "string" };
  }

  if (typeof value === "boolean") {
    return { type: "boolean" };
  }

  if (typeof value === "number") {
    return { type: "number" };
  }

  if (Array.isArray(value)) {
    return { type: "list" };
  }

  if (isPlainObject(value)) {
    if (isCurrencyObject(value)) {
      return { type: "currency", symbol: value.symbol };
    }
    return { type: "object" };
  }

  if (typeof value === "string" && isISO8601(value)) {
    return { type: "datetime" };
  }

  return { type: "string" };
};

const ALL_CELL_TYPES: ReadonlyArray<CellDisplayType["type"]> = [
  "string",
  "number",
  "boolean",
  "date",
  "datetime",
  "currency",
  "enum",
  "tags",
  "list",
  "object",
] as const;

// Types that render the same value the same way, so switching adds nothing.
// Number and currency are deliberately not a pair: the symbol is a real
// presentation change, and the amount converts both ways.
const INTERCHANGEABLE_TYPE_PAIRS: ReadonlyArray<
  readonly [CellDisplayType["type"], CellDisplayType["type"]]
> = [
  ["string", "enum"],
  ["string", "tags"],
  ["tags", "list"],
] as const;

const isInterchangeable = (
  a: CellDisplayType["type"],
  b: CellDisplayType["type"],
): boolean =>
  a !== b &&
  INTERCHANGEABLE_TYPE_PAIRS.some(
    (pair) => pair.includes(a) && pair.includes(b),
  );

/** Override options minus interchangeable twins of the current AND declared
 *  type — overrides aren't persisted, so a declared-type twin would revert
 *  after saving. The declared type itself stays selectable. */
export const getAvailableTypeOverrides = (
  currentType: CellDisplayType["type"],
  declaredType: CellDisplayType["type"],
): ReadonlyArray<CellDisplayType["type"]> =>
  ALL_CELL_TYPES.filter(
    (type) =>
      type !== currentType &&
      !isInterchangeable(type, currentType) &&
      !isInterchangeable(type, declaredType),
  );

const CELL_TYPE_ICONS: Record<CellDisplayType["type"], IconDefinition> = {
  string: TYPE_ICONS.string,
  number: TYPE_ICONS.number,
  boolean: TYPE_ICONS.boolean,
  date: TYPE_ICONS.date,
  datetime: TYPE_ICONS.datetime,
  currency: TYPE_ICONS.number,
  enum: TYPE_ICONS.enum,
  tags: TYPE_ICONS.tag,
  list: TYPE_ICONS.list,
  object: TYPE_ICONS.object,
};

export const getCellTypeIcon = (
  type: CellDisplayType["type"],
): IconDefinition => CELL_TYPE_ICONS[type];
